import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import {
  setupCanvas,
  roundedRect,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, formatNumber } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import type { ChartDataPoint, ChartOptions, AxisOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// BarChart – Canvas-rendered vertical bar chart
// ---------------------------------------------------------------------------

interface BarChartSignature {
  Args: ChartOptions &
    AxisOptions & {
      /** Chart data points */
      data: ChartDataPoint[];
      /** Fixed height in pixels (default: 300) */
      height?: number;
    };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 300;

export default class BarChart extends Component<BarChartSignature> {
  @tracked containerWidth = 400;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  get height() {
    return this.args.height ?? DEFAULT_HEIGHT;
  }

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get data() {
    return this.args.data ?? [];
  }

  get shouldRotateLabels() {
    return this.data.length > 5;
  }

  get canvasStyle() {
    return htmlSafe(`width: ${this.containerWidth}px; height: ${this.height}px;`);
  }

  get tooltipData() {
    if (this.hoveredIndex === null || !this.data[this.hoveredIndex]) return null;
    const d = this.data[this.hoveredIndex]!;
    return { category: d.category, value: formatNumber(d.value) };
  }

  get tooltipStyle() {
    if (!this.tooltipData) return htmlSafe('display: none;');
    return htmlSafe(`left: ${this.tooltipX}px; top: ${this.tooltipY}px;`);
  }

  installCanvas = modifier((element: HTMLElement) => {
    const canvas = element.querySelector('canvas');
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      this.draw(canvas);
    });
    observer.observe(element);
    next(this, () => {
      this.containerWidth = element.clientWidth || 400;
      this.draw(canvas);
    });

    // Mouse interaction
    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
      this.draw(canvas);
    };
    const onClick = (e: MouseEvent) => this.handleClick(e, canvas);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  });

  // Store bar rects for hit testing
  private barRects: Array<{ x: number; y: number; w: number; h: number; index: number }> = [];

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const data = this.data;
    if (!data.length) return;

    const bottomMargin = this.shouldRotateLabels ? 80 : 40;
    const layout = computeLayout(width, height, { bottom: bottomMargin, left: 55 });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // Scales
    const maxVal = Math.max(...data.map((d) => d.value), 0);
    const ticks = niceAxisTicks(0, maxVal);
    const yScale = linearScale(
      [0, ticks[ticks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );
    const categories = data.map((d) => d.category);
    const xScale = bandScale(categories, [layout.left, layout.left + layout.innerWidth], 0.2);

    // Grid lines
    drawGridLines(ctx, ticks, yScale, layout.left, layout.left + layout.innerWidth, theme.grid);

    // Y axis labels
    const formatFn = this.args.formatValue ?? formatAxisValue;
    drawYAxisLabels(ctx, ticks, yScale, layout.left - 8, theme.textMuted, formatFn);

    // X axis labels
    const positions = categories.map((c) => xScale(c) + xScale.bandwidth() / 2);
    drawXAxisLabels(
      ctx,
      categories,
      positions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      this.shouldRotateLabels
    );

    // Bars
    this.barRects = [];
    const bw = xScale.bandwidth();
    data.forEach((d, i) => {
      const x = xScale(d.category);
      const barH = layout.top + layout.innerHeight - yScale(d.value);
      const y = yScale(d.value);
      const color = this.colors[i % this.colors.length]!;

      ctx.fillStyle = this.hoveredIndex === i ? adjustBrightness(color, 1.15) : color;
      roundedRect(ctx, x, y, bw, barH, 4);
      ctx.fill();

      this.barRects.push({ x, y, w: bw, h: barH, index: i });
    });

    // Axis labels
    if (this.args.xLabel) {
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, height - 4);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.fillStyle = theme.text;
      ctx.font = '12px system-ui, sans-serif';
      ctx.translate(14, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit) {
      this.hoveredIndex = hit.index;
      this.tooltipX = rect.left + hit.x + hit.w / 2;
      this.tooltipY = rect.top + hit.y - 8;
      this.draw(canvas);
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  handleClick(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hit = this.barRects.find((r) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);

    if (hit && this.args.onClick && this.data[hit.index]) {
      this.args.onClick(this.data[hit.index]!);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="bar"
      class="relative w-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas role="img" aria-label="Bar chart" style={{this.canvasStyle}}></canvas>

      {{! Tooltip overlay }}
      {{#if this.tooltipData}}
        <div
          data-test-chart-tooltip
          class="fixed z-50 pointer-events-none px-2.5 py-1.5 rounded-lg text-xs shadow-lg dark:shadow-black/40 border border-default bg-surface-elevated tio-text-default -translate-x-1/2 -translate-y-full"
          style={{this.tooltipStyle}}
        >
          <div class="font-medium">{{this.tooltipData.category}}</div>
          <div class="tio-text-muted">{{this.tooltipData.value}}</div>
        </div>
      {{/if}}
    </div>
  </template>
}

function adjustBrightness(hex: string, factor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v * factor)));
  return `rgb(${clamp(r)}, ${clamp(g)}, ${clamp(b)})`;
}

export type { BarChartSignature };
